import {
  Component,
  Input,
  OnInit,
  forwardRef,
  inject,
} from '@angular/core';
import {
  ControlValueAccessor,
  FormGroup,
  NG_VALUE_ACCESSOR,
} from '@angular/forms';

import { Subscription } from 'rxjs';

import { FormsService } from 'src/app/core/services/forms.service';
import { UseCaseFormMapperService } from 'src/app/core/services/use-case-form-mapper.service';
import { FormProperty } from 'src/app/core/models/form.models';
import { UseCaseInputType } from 'src/app/core/models/use-case.models';

/**
 * Structured repeater for a tag_list field carrying an itemShape (§4.2/§15,
 * e.g. competitors: {name, keywords}). Renders repeatable rows; each row is a
 * FormGroup built from the itemShape via the shared mapper (name -> text,
 * keywords -> tag-input) and rendered through the existing `<app-dynamic-form>`
 * controls. Generic over the itemShape keys so future templates reuse it. Emits
 * the array shape the backend InputValidator expects: [{<key>: value, ...}].
 */
@Component({
  selector: 'app-use-case-item-list',
  templateUrl: './use-case-item-list.component.html',
  styleUrls: ['./use-case-item-list.component.scss'],
  standalone: false,
  providers: [
    {
      provide: NG_VALUE_ACCESSOR,
      useExisting: forwardRef(() => UseCaseItemListComponent),
      multi: true,
    },
  ],
})
export class UseCaseItemListComponent
  implements OnInit, ControlValueAccessor
{
  @Input() itemShape: Record<string, UseCaseInputType> = {};
  @Input() label = '';
  @Input() addLabelKey = 'use-case-wizard.item-list.add-item';

  private readonly formsService = inject(FormsService);
  private readonly mapper = inject(UseCaseFormMapperService);

  rowProperties: FormProperty[] = [];
  rows: FormGroup[] = [];
  private rowSubscriptions: Subscription[] = [];
  private disabled = false;

  private onChange: (value: unknown[]) => void = () => {};
  private onTouched: () => void = () => {};

  ngOnInit(): void {
    this.rowProperties = this.mapper.mapItemShapeToProperties(this.itemShape);
  }

  /**
   * ControlValueAccessor: rebuild the rows from the incoming value without
   * emitting (avoids a write feedback loop).
   *
   * @param value the array of item records
   * @return void
   */
  writeValue(value: unknown[]): void {
    this.clearRows();
    (Array.isArray(value) ? value : []).forEach((item) =>
      this.rows.push(this.buildRow(item as Record<string, unknown>)),
    );
  }

  /**
   * ControlValueAccessor registration.
   *
   * @param fn the change callback
   * @return void
   */
  registerOnChange(fn: (value: unknown[]) => void): void {
    this.onChange = fn;
  }

  /**
   * ControlValueAccessor registration.
   *
   * @param fn the touched callback
   * @return void
   */
  registerOnTouched(fn: () => void): void {
    this.onTouched = fn;
  }

  /**
   * ControlValueAccessor: reflect the disabled state onto every row.
   *
   * @param isDisabled the disabled flag
   * @return void
   */
  setDisabledState(isDisabled: boolean): void {
    this.disabled = isDisabled;
    this.rows.forEach((row) =>
      isDisabled ? row.disable({ emitEvent: false }) : row.enable({ emitEvent: false }),
    );
  }

  /** Append an empty row and emit. */
  addRow(): void {
    this.rows.push(this.buildRow({}));
    this.emit();
  }

  /**
   * Remove a row by index and emit.
   *
   * @param index the row index
   * @return void
   */
  removeRow(index: number): void {
    const [removed] = this.rows.splice(index, 1);
    this.rowSubscriptions[index]?.unsubscribe();
    this.rowSubscriptions.splice(index, 1);
    if (removed) {
      this.emit();
    }
  }

  /**
   * Build a row FormGroup from the itemShape and track its changes.
   *
   * @param value the initial row value
   * @returns the row FormGroup
   */
  private buildRow(value: Record<string, unknown>): FormGroup {
    const group = this.formsService.createFormGroup(this.rowProperties);
    group.patchValue(value ?? {}, { emitEvent: false });
    if (this.disabled) {
      group.disable({ emitEvent: false });
    }
    this.rowSubscriptions.push(group.valueChanges.subscribe(() => this.emit()));

    return group;
  }

  /** Emit the aggregate array value and mark touched. */
  private emit(): void {
    this.onChange(this.rows.map((row) => row.getRawValue()));
    this.onTouched();
  }

  /** Tear down all rows and their subscriptions. */
  private clearRows(): void {
    this.rowSubscriptions.forEach((sub) => sub.unsubscribe());
    this.rowSubscriptions = [];
    this.rows = [];
  }
}
